import React, { useMemo, useRef, useState } from 'react';

import type { SectorPerformancePoint } from '../types';
import { ChartDownloadMenu } from '../../../../shared/ui';
import { useChartExport } from '../../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../../shared/utils/chartFilename';

type TimeframeKey =
    | 'ret_d1'
    | 'ret_7d'
    | 'ret_1m'
    | 'ret_3m'
    | 'ret_6m'
    | 'ret_ytd'
    | 'ret_1y'
    | 'ret_3y';

const TIMEFRAMES: { label: string; value: TimeframeKey }[] = [
    { label: '1D', value: 'ret_d1' },
    { label: '7D', value: 'ret_7d' },
    { label: '1M', value: 'ret_1m' },
    { label: '3M', value: 'ret_3m' },
    { label: '6M', value: 'ret_6m' },
    { label: 'YTD', value: 'ret_ytd' },
    { label: '1Y', value: 'ret_1y' },
    { label: '3Y', value: 'ret_3y' },
];

interface SectorPerformanceProps {
    data: SectorPerformancePoint[] | null | undefined;
}

export const SectorPerformance: React.FC<SectorPerformanceProps> = ({ data }) => {
    const [selectedTf, setSelectedTf] = useState<TimeframeKey>('ret_1m');

    const containerRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(containerRef);
    const tfLabel = TIMEFRAMES.find((t) => t.value === selectedTf)?.label ?? selectedTf;

    // 1. Sort dynamically: best performers on the left, worst on the right.
    const sortedData = useMemo(() => {
        if (!data) return [];
        return [...data].sort((a, b) => b[selectedTf] - a[selectedTf]);
    }, [data, selectedTf]);

    // 2. Find the max absolute value to scale the Y-axis bounds proportionally.
    const maxAbs = useMemo(() => {
        if (!sortedData.length) return 1;
        const max = Math.max(...sortedData.map((d) => Math.abs(d[selectedTf])));
        return max === 0 ? 1 : max; // Prevent division by zero.
    }, [sortedData, selectedTf]);

    if (!data || data.length === 0) return null;

    return (
        <div className="flex flex-col w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm">
            {/* --- Header & Toggles --- */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800 gap-4">
                <div>
                    <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                        Sector Performance
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Historical total return by actively trading windows.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    {/* Timeframe Toggle Pills */}
                    <div className="flex flex-wrap bg-slate-100 dark:bg-slate-800/80 p-1 rounded-lg border border-slate-200 dark:border-slate-700/50">
                        {TIMEFRAMES.map((tf) => {
                            const isActive = selectedTf === tf.value;
                            return (
                                <button
                                    key={tf.value}
                                    type="button"
                                    onClick={() => setSelectedTf(tf.value)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ease-out ${
                                        isActive
                                            ? 'bg-white dark:bg-slate-600 text-indigo-700 dark:text-indigo-300 shadow-sm ring-1 ring-slate-200 dark:ring-slate-500'
                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                                    }`}
                                >
                                    {tf.label}
                                </button>
                            );
                        })}
                    </div>
                    <ChartDownloadMenu
                        label="Sector Performance chart"
                        onPng={() => downloadPng({ filename: buildChartFilename('sector-performance', [tfLabel]) })}
                        onJpeg={() => downloadJpeg({ filename: buildChartFilename('sector-performance', [tfLabel]) })}
                    />
                </div>
            </div>

            {/* --- Chart Area --- */}
            <div ref={containerRef} className="p-6 relative overflow-x-auto bg-white dark:bg-slate-900">
                <div className="min-w-[600px] h-[300px] relative flex flex-col justify-center">
                    {/* Background Grid Lines */}
                    <div className="absolute inset-0 flex flex-col justify-between pointer-events-none z-0 py-6">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div
                                key={i}
                                className="w-full border-t border-dashed border-slate-100 dark:border-slate-800/60 h-0"
                            ></div>
                        ))}
                    </div>

                    {/* Central Zero Line */}
                    <div className="absolute top-1/2 left-0 right-0 border-t-2 border-slate-200 dark:border-slate-700 z-10 shadow-sm"></div>

                    {/* Bars Container */}
                    <div className="relative z-20 w-full h-full flex items-center justify-around px-2 py-6">
                        {sortedData.map((sector) => {
                            const val = sector[selectedTf];
                            const isPositive = val >= 0;
                            // 100% height inside the half-container represents
                            // hitting the absolute top/bottom margin.
                            const heightPct = Math.max((Math.abs(val) / maxAbs) * 100, 1);
                            const valStr = `${(val * 100).toFixed(1)}%`;

                            return (
                                <div
                                    key={sector.ticker}
                                    className="relative flex flex-col items-center h-full w-[6%] max-w-[48px] group"
                                >
                                    {/* Top Half (Positive Values) */}
                                    <div className="w-full h-1/2 flex flex-col justify-end items-center pb-[1px]">
                                        {isPositive && (
                                            <>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute top-0 -mt-6 whitespace-nowrap bg-slate-800 dark:bg-slate-700 text-white text-[10px] font-bold py-1 px-2 rounded shadow-lg z-50 pointer-events-none">
                                                    {sector.name}:{' '}
                                                    <span className="text-emerald-400">
                                                        +{valStr}
                                                    </span>
                                                </div>
                                                <div
                                                    style={{ height: `${heightPct}%` }}
                                                    className="w-full bg-gradient-to-t from-emerald-500 to-emerald-400 dark:from-emerald-600 dark:to-emerald-400 rounded-t-sm shadow-sm transition-all duration-500 ease-out group-hover:brightness-110"
                                                ></div>
                                            </>
                                        )}
                                    </div>

                                    {/* Bottom Half (Negative Values) */}
                                    <div className="w-full h-1/2 flex flex-col justify-start items-center pt-[1px]">
                                        {!isPositive && (
                                            <>
                                                <div
                                                    style={{ height: `${heightPct}%` }}
                                                    className="w-full bg-gradient-to-b from-rose-400 to-rose-500 dark:from-rose-500 dark:to-rose-600 rounded-b-sm shadow-sm transition-all duration-500 ease-out group-hover:brightness-110"
                                                ></div>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute bottom-0 -mb-6 whitespace-nowrap bg-slate-800 dark:bg-slate-700 text-white text-[10px] font-bold py-1 px-2 rounded shadow-lg z-50 pointer-events-none">
                                                    {sector.name}:{' '}
                                                    <span className="text-rose-400">{valStr}</span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* --- X-Axis Labels --- */}
                <div className="flex justify-around mt-2 border-t border-slate-100 dark:border-slate-800/60 pt-4 min-w-[600px]">
                    {sortedData.map((sector) => {
                        const val = sector[selectedTf];
                        const isPositive = val >= 0;
                        const valStr = `${(val * 100).toFixed(1)}%`;
                        return (
                            <div
                                key={`label-${sector.ticker}`}
                                className="flex flex-col items-center w-[6%] max-w-[48px] cursor-default"
                            >
                                <span className="text-xs font-extrabold text-slate-700 dark:text-slate-300">
                                    {sector.ticker}
                                    {sector.defensive && (
                                        <span className="text-indigo-400">*</span>
                                    )}
                                </span>
                                <span
                                    className={`text-[10px] font-mono font-semibold mt-0.5 ${
                                        isPositive
                                            ? 'text-emerald-600 dark:text-emerald-500'
                                            : 'text-rose-600 dark:text-rose-500'
                                    }`}
                                >
                                    {isPositive ? '+' : ''}
                                    {valStr}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Legend Footer */}
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 flex items-center justify-between rounded-b-xl">
                <div>
                    <span className="font-bold text-indigo-400">*</span> Indicates a defensive
                    sector
                </div>
                <div>Dynamic sorting by timeframe</div>
            </div>
        </div>
    );
};

export default SectorPerformance;
